import React, { useLayoutEffect, useMemo, useState } from 'react'
import { ParticipantView } from './ParticipantView'
import { StartingCountdown } from './StartingCountdown'
import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useTurns } from '@/contexts/TurnsProvider'
import { SpeakingRole, TurnStatus } from '@db8/shared'

/** Measure a container by DOM id, with safe fallbacks */
export function useContainerDimensionsById(id: string) {
  const [width, setWidth] = useState<number | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  const { hallUsers, moderatorUsers } = useHallUsers()
  const { pinnedParticipants } = useTurns()

  useLayoutEffect(() => {
    if (!id) return
    const el = document.getElementById(id)
    if (!el) return

    const update = () => {
      // fall back to boundingRect if client* are 0 for any reason
      const rect = el.getBoundingClientRect()
      const h = el.clientHeight || Math.round(rect.height)
      const w = el.clientWidth || Math.round(rect.width)
      setHeight(h || null)
      setWidth(w || null)
    }

    update()

    const ro = new ResizeObserver(update)
    ro.observe(el)
    const raf = requestAnimationFrame(update)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [id, hallUsers, pinnedParticipants, moderatorUsers.length])

  return { containerWidth: width, containerHeight: height }
}

const MemoizedParticipant = React.memo(
  ParticipantView,
  (a, b) => a.participantId === b.participantId,
)

export const ParticipantGrid = ({
  participantIds,
  isPresenting,
  type = '',
  containerId = '',
  emptyPlaceholderSrc,
}: {
  participantIds: string[]
  isPresenting: boolean
  type?: string
  containerId?: string
  emptyPlaceholderSrc?: string
}) => {
  const { hallMetadata } = useHall()
  const { getUser } = useHallUsers()
  const { pinnedParticipants, currentTurn, status } = useTurns()
  const { containerWidth, containerHeight } = useContainerDimensionsById(containerId || '')

  const showCountdown = useMemo(() => {
    if (
      pinnedParticipants.every((id) => participantIds.includes(id)) &&
      status === TurnStatus.TURN_STARTING
    )
      return true
  }, [status, currentTurn, pinnedParticipants, participantIds])

  const countdownColor = useMemo(() => {
    if (pinnedParticipants.length > 1) return SpeakingRole.Moderator
    else if (participantIds.length === 1) {
      const user = getUser(pinnedParticipants[0])
      return user?.speakingRole as SpeakingRole
    }
  }, [pinnedParticipants])

  /** ---- Moderators lane (single row) ---- */
  if (type === 'moderators') {
    // If the container hasn’t reported a height yet, use a safe fallback.
    const tileMin = 180
    const laneHeight = Math.max(tileMin, containerHeight ?? tileMin)
    // Respect vertical padding inside the container (e.g., p-2 => 8px top + 8px bottom)
    const verticalPaddingPx = 16
    const squareSize = Math.max(tileMin, laneHeight - verticalPaddingPx)

    // Empty state: show an empty chair when no judges/moderators are present.
    if (participantIds.length === 0) {
      return (
        <div className="flex justify-center space-x-4 w-full h-full items-center">
          <div className="relative flex items-stretch" style={{ height: squareSize, width: squareSize }}>
            <div className="flex items-center justify-center h-full w-full relative">
              <img
                // For judges/moderators empty gallery, always use the *empty* chair (no card),
                // not a stage placeholder that might be passed in.
                src={encodeURI('/Empty Chair - No card.png')}
                alt="Empty chair"
                className="object-contain max-w-full max-h-full"
              />
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="flex justify-center space-x-4 w-full h-full items-center">
        {participantIds.map((participantId, index) => (
          <div
            key={participantId}
            className="relative flex items-stretch"
            style={{ height: squareSize, width: squareSize }}
          >
            <ParticipantView
              participantId={participantId}
              type={type}
              participantLength={participantIds.length}
              containerWidth={squareSize}
              containerHeight={squareSize}
              index={index}
            />
          </div>
        ))}
      </div>
    )
  }

  /** ---- Everyone else (auto grid) ---- */
  const count = participantIds.length

  // Responsive layout: orient tiles along the *longest* container dimension.
  // Wider-than-tall => favor more columns (side-by-side). Taller-than-wide => favor more rows (stacked).
  const aspect =
    containerWidth && containerHeight && containerHeight > 0
      ? containerWidth / containerHeight
      : 1

  let cols = 1
  let rows = 1

  if (count === 1) {
    cols = 1
    rows = 1
  } else {
    if (aspect >= 1) {
      // wide: bias towards more columns
      cols = Math.min(count, Math.max(1, Math.ceil(Math.sqrt(count * aspect))))
      rows = Math.ceil(count / cols)
    } else {
      // tall: bias towards more rows (stack)
      rows = Math.min(count, Math.max(1, Math.ceil(Math.sqrt(count / aspect))))
      cols = Math.ceil(count / rows)
    }

    // Keep 3-person layout centered nicely (existing behavior)
    if (count === 3) {
      cols = 2
      rows = 2
    }
  }

  // When no participants to show (e.g., solo teammate is on stage), render a placeholder avatar
  if (count === 0) {
    return (
      <div className="relative w-full h-full min-h-0">
        <div
          className="participant-grid w-full h-full min-h-0"
          style={{
            // @ts-ignore custom props consumed by your CSS
            '--columns': 1,
            '--rows': 1,
          }}
        >
          <div className="relative h-full min-w-[120px] min-h-[140px]">
            <div className="flex items-center justify-center h-full w-full relative">
              <img
                src={encodeURI(emptyPlaceholderSrc || '/Empty Chair - No card.png')}
                alt="Empty chair"
                className="object-cover rounded-full max-w-[70%] max-h-[70%] border-2 border-gray-400"
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full min-h-0">
      <div
        className="participant-grid w-full h-full min-h-0"
        style={{
          // @ts-ignore custom props consumed by your CSS
          '--columns': cols,
          '--rows': rows,
          width: count === 2 ? '100%' : undefined,
          height: count === 2 ? '100%' : undefined,
        }}
      >
        {participantIds.map((participantId, index) => {
          const extraStyle =
            count === 3 && index === 2
              ? { gridColumn: '1 / span 2', display: 'flex', justifyContent: 'center' }
              : {}
          return (
            <div
              key={participantId}
              className="relative h-full min-w-[120px] min-h-[140px]"
              style={extraStyle}
            >
              <ParticipantView
                participantId={participantId}
                type={type}
                participantLength={participantIds.length}
                containerWidth={containerWidth ?? 480}
                containerHeight={containerHeight ?? 320}
                index={index}
              />
            </div>
          )
        })}
      </div>

      {showCountdown && <StartingCountdown speakingRole={countdownColor} />}
    </div>
  )
}

export const MemoizedParticipantGrid = React.memo(ParticipantGrid, (prev, next) => {
  return (
    JSON.stringify(prev.participantIds) === JSON.stringify(next.participantIds) &&
    prev.isPresenting === next.isPresenting &&
    prev.type === next.type
  )
})
